import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const TrendAnalytics = () => {
  const { getAccountId } = useAuth();
  const [view, setView] = useState('gallery');
  const [selectedTrend, setSelectedTrend] = useState(null);
  const [category, setCategory] = useState('hook'); // hook, messaging, concept
  // Separate input filters (what user types) from applied filters (what triggers API)
  const [filterInputs, setFilterInputs] = useState({
    adName: '',
    campaignName: '',
    minSpend: 0,
    minROAS: 0
  });
  const [appliedFilters, setAppliedFilters] = useState({
    adName: '',
    campaignName: '',
    minSpend: 0,
    minROAS: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [trends, setTrends] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedConcepts, setGeneratedConcepts] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null); // { url, type, adName }
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all'); // 'all', 'image', 'video'

  // Fetch trends from API - only when category or applied filters change
  useEffect(() => {
    loadTrends();
  }, [category, appliedFilters]);

  const loadTrends = async () => {
    setIsLoading(true);
    try {
      const accountId = getAccountId();
      if (!accountId) {
        console.error('No account ID available');
        setIsLoading(false);
        return;
      }

      const response = await fetch('/api/trend-analytics/trends', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-account-id': accountId
        },
        body: JSON.stringify({
          accountId,
          category,
          minSpend: parseFloat(appliedFilters.minSpend) || 0,
          minROAS: parseFloat(appliedFilters.minROAS) || 0,
          minAds: 2
        })
      });

      const data = await response.json();
      if (data.success) {
        // Filter trends by additional filters
        let filtered = data.data.trends;
        
        if (appliedFilters.adName) {
          filtered = filtered.map(trend => ({
            ...trend,
            matchingAds: trend.matchingAds.filter(ad => 
              ad.ad_name.toLowerCase().includes(appliedFilters.adName.toLowerCase())
            )
          })).filter(trend => trend.matchingAds.length > 0);
        }

        if (appliedFilters.campaignName) {
          filtered = filtered.map(trend => ({
            ...trend,
            matchingAds: trend.matchingAds.filter(ad => 
              ad.campaign_name?.toLowerCase().includes(appliedFilters.campaignName.toLowerCase())
            )
          })).filter(trend => trend.matchingAds.length > 0);
        }

        // Note: minSpend and minROAS are already applied at SQL level to individual ads
        // But when we filter by adName/campaignName, we need to ensure the filtered ads still meet thresholds
        const minSpendNum = parseFloat(appliedFilters.minSpend) || 0;
        const minROASNum = parseFloat(appliedFilters.minROAS) || 0;
        
        // Update metrics based on filtered ads
        filtered = filtered.map(trend => {
          const matchingAds = trend.matchingAds;
          if (matchingAds.length === 0) return null;

          // Ensure all ads in the filtered set still meet the minSpend threshold
          const validAds = matchingAds.filter(ad => {
            const adSpend = parseFloat(ad.total_spend || 0);
            const adROAS = parseFloat(ad.roas || 0);
            return adSpend >= minSpendNum && (minROASNum === 0 || adROAS >= minROASNum);
          });

          if (validAds.length === 0) return null;

          const totalSpend = validAds.reduce((sum, ad) => sum + parseFloat(ad.total_spend || 0), 0);
          const totalRevenue = validAds.reduce((sum, ad) => sum + parseFloat(ad.total_revenue || 0), 0);
          const roas = totalSpend > 0 ? totalRevenue / totalSpend : 0;

          return {
            ...trend,
            matchingAds: validAds,
            metrics: {
              ...trend.metrics,
              totalAds: validAds.length,
              avgSpend: totalSpend / validAds.length,
              avgRevenue: totalRevenue / validAds.length,
              roas: parseFloat(roas.toFixed(2)),
              totalSpend,
              totalRevenue
            }
          };
        }).filter(trend => trend !== null);

        setTrends(filtered);
      }
    } catch (error) {
      console.error('Error loading trends:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Sparkline component
  const Sparkline = ({ data, color, height = 40 }) => {
    if (!data || data.length === 0) return null;
    
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const width = 120;
    const padding = 4;
    
    const points = data.map((value, index) => {
      const x = (index / (data.length - 1)) * (width - padding * 2) + padding;
      const y = height - ((value - min) / range) * (height - padding * 2) - padding;
      return `${x},${y}`;
    }).join(' ');
    
    return (
      <svg width={width} height={height} className="overflow-visible">
        <defs>
          <linearGradient id={`gradient-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.4" />
            <stop offset="100%" stopColor={color} stopOpacity="0.05" />
          </linearGradient>
        </defs>
        <polyline
          points={`0,${height} ${points} ${width},${height}`}
          fill={`url(#gradient-${color})`}
          stroke="none"
        />
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  // Generate variations handler - now uses AI to create actionable, tailor-made concepts
  const handleGenerateVariations = async () => {
    setIsGenerating(true);
    try {
      const accountId = getAccountId();
      if (!accountId) {
        console.error('No account ID available');
        setIsGenerating(false);
        return;
      }

      // Get sample ads from this trend for context
      const sampleAds = selectedTrend.matchingAds.slice(0, 5).map(ad => ({
        ad_name: ad.ad_name,
        hook_pattern: ad.hook_pattern,
        messaging_pattern: ad.messaging_pattern,
        concept_pattern: ad.concept_pattern,
        visual_description: ad.visual_description,
        text_overlay_description: ad.text_overlay_description,
        audio_description: ad.audio_description
      }));

      const response = await fetch('/api/trend-analytics/generate-variations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-account-id': accountId
        },
        body: JSON.stringify({
          accountId,
          trendName: selectedTrend.name,
          trendDescription: selectedTrend.description,
          patternCategory: selectedTrend.category,
          patternValue: selectedTrend.pattern,
          sampleAds,
          metrics: selectedTrend.metrics
        })
      });

      const data = await response.json();
      if (data.success) {
        setGeneratedConcepts(data.data.concepts);
      } else {
        console.error('Error generating concepts:', data.error);
        // Fallback to basic concepts
        setGeneratedConcepts(Array.from({ length: 5 }, (_, i) => ({
          id: i + 1,
          title: `${selectedTrend.name} - Variation ${i + 1}`,
          hook: `Hook following the "${selectedTrend.pattern}" pattern with engaging opening`,
          visual: `Visual elements inspired by "${selectedTrend.pattern}" pattern`,
          messaging: `Messaging that leverages the "${selectedTrend.pattern}" trend`,
          cta: "Get Started"
        })));
      }
    } catch (error) {
      console.error('Error generating variations:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Apply filters - copy input state to applied state
  const handleApplyFilters = () => {
    setAppliedFilters({ ...filterInputs });
  };

  // Clear all filters
  const handleClearFilters = () => {
    const clearedFilters = { adName: '', campaignName: '', minSpend: 0, minROAS: 0 };
    setFilterInputs(clearedFilters);
    setAppliedFilters(clearedFilters);
  };

  // Helper function to convert concept field value to string (handles objects from LLM)
  const formatConceptField = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      // Handle structured objects from LLM
      if (value.description) return value.description;
      if (value.type && value.duration_seconds) {
        return `${value.type} (${value.duration_seconds}s): ${value.description || ''}`;
      }
      if (value.pacing || value.key_scenes || value.lighting) {
        const parts = [];
        if (value.pacing) parts.push(`Pacing: ${value.pacing}`);
        if (value.lighting) parts.push(`Lighting: ${value.lighting}`);
        if (value.key_scenes) parts.push(`Key Scenes: ${value.key_scenes}`);
        return parts.join(' | ');
      }
      // Fallback: try to extract meaningful text
      const textValues = Object.values(value).filter(v => typeof v === 'string' && v.length > 0);
      if (textValues.length > 0) return textValues.join(' | ');
      // Last resort: JSON string
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  // Helper function to check if media is video
  const isVideoMedia = (mediaFormat, assetUrl) => {
    return mediaFormat === 'Video' || assetUrl?.includes('.mp4') || assetUrl?.includes('.mov');
  };

  // Filter ads by media type
  const getFilteredAds = (ads) => {
    if (mediaTypeFilter === 'all') return ads;
    return ads.filter(ad => {
      const isVideo = isVideoMedia(ad.media_format, ad.asset_url);
      if (mediaTypeFilter === 'video') return isVideo;
      if (mediaTypeFilter === 'image') return !isVideo;
      return true;
    });
  };

  // Render pattern icon using first ad's media
  const renderPatternIcon = (trend) => {
    const firstAd = trend.matchingAds && trend.matchingAds.length > 0 ? trend.matchingAds[0] : null;
    const iconAssetUrl = firstAd?.asset_url;
    const iconMediaFormat = firstAd?.media_format;
    const isVideoIcon = iconAssetUrl && isVideoMedia(iconMediaFormat, iconAssetUrl);
    
    if (!iconAssetUrl) {
      // Fallback to placeholder icon if no ad media available
      return (
        <div className={`w-20 h-20 bg-gradient-to-br ${trend.color} rounded-2xl flex items-center justify-center text-4xl shadow-lg flex-shrink-0 group-hover:scale-110 transition-transform`}>
          {trend.icon}
        </div>
      );
    }
    
    return (
      <div className="w-20 h-20 rounded-2xl overflow-hidden bg-zinc-800 flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg border border-zinc-700">
        {isVideoIcon ? (
          <video
            src={iconAssetUrl}
            className="w-full h-full object-cover"
            muted
            playsInline
            onMouseEnter={(e) => {
              const video = e.target;
              const playPromise = video.play();
              if (playPromise !== undefined) {
                playPromise.catch((error) => {
                  // Play was interrupted or failed, ignore error
                  // This is expected when pause() is called quickly
                });
              }
            }}
            onMouseLeave={(e) => {
              const video = e.target;
              // Pause the video
              const pausePromise = video.pause();
              if (pausePromise !== undefined) {
                pausePromise.catch(() => {
                  // Pause error, ignore
                });
              }
              // Reset to beginning
              video.currentTime = 0;
            }}
          />
        ) : (
          <img
            src={iconAssetUrl}
            alt={firstAd?.ad_name || 'Ad preview'}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to placeholder if image fails to load
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = `<div class="w-full h-full bg-gradient-to-br ${trend.color} rounded-2xl flex items-center justify-center text-4xl">${trend.icon}</div>`;
            }}
          />
        )}
      </div>
    );
  };

  // Render media preview
  const renderMediaPreview = (assetUrl, mediaFormat, adName) => {
    if (!assetUrl) {
      return (
        <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-lg flex items-center justify-center">
          <span className="text-4xl opacity-30">📷</span>
        </div>
      );
    }

    const isVideo = isVideoMedia(mediaFormat, assetUrl);

    return (
      <div className="w-full h-full rounded-lg overflow-hidden bg-zinc-800">
        {isVideo ? (
          <video
            src={assetUrl}
            className="w-full h-full object-cover"
            muted
            playsInline
            onMouseEnter={(e) => {
              const video = e.target;
              const playPromise = video.play();
              if (playPromise !== undefined) {
                playPromise.catch((error) => {
                  // Play was interrupted or failed, ignore error
                  // This is expected when pause() is called quickly
                });
              }
            }}
            onMouseLeave={(e) => {
              const video = e.target;
              // Pause the video
              const pausePromise = video.pause();
              if (pausePromise !== undefined) {
                pausePromise.catch(() => {
                  // Pause error, ignore
                });
              }
              // Reset to beginning
              video.currentTime = 0;
            }}
          />
        ) : (
          <img
            src={assetUrl}
            alt={adName}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-lg flex items-center justify-center"><span class="text-4xl opacity-30">📷</span></div>';
            }}
          />
        )}
      </div>
    );
  };

  // Media Preview Modal
  const MediaPreviewModal = () => {
    if (!previewMedia) return null;

    const isVideo = previewMedia.type === 'Video' || previewMedia.url?.includes('.mp4') || previewMedia.url?.includes('.mov');

    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
        onClick={() => setPreviewMedia(null)}
      >
        <div 
          className="relative max-w-4xl w-full mx-4 bg-zinc-900 rounded-2xl border border-zinc-800 shadow-2xl flex flex-col max-h-[90vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={() => setPreviewMedia(null)}
            className="absolute top-4 right-4 z-10 w-10 h-10 bg-zinc-800/80 hover:bg-zinc-700 rounded-full flex items-center justify-center transition-colors"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Media Content */}
          <div className="relative bg-zinc-950 overflow-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
            <div className="flex items-center justify-center min-h-[300px] p-4">
              {isVideo ? (
                <video
                  src={previewMedia.url}
                  style={{ 
                    maxWidth: '100%',
                    maxHeight: 'calc(90vh - 250px)',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block'
                  }}
                  controls
                  autoPlay
                  loop
                />
              ) : (
                <img
                  src={previewMedia.url}
                  alt={previewMedia.adName}
                  style={{ 
                    maxWidth: '100%',
                    maxHeight: 'calc(90vh - 250px)',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block'
                  }}
                />
              )}
            </div>
          </div>

          {/* Ad Details */}
          <div className="p-6 border-t border-zinc-800">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2">{previewMedia.adName}</h3>
                <p className="text-sm text-zinc-400 mb-4">{previewMedia.campaignName}</p>
                
                {/* Pattern Labels */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {previewMedia.hookPattern && (
                    <span className="px-3 py-1 bg-cyan-500/20 border border-cyan-500/30 rounded-full text-xs text-cyan-400">
                      Hook: {previewMedia.hookPattern}
                    </span>
                  )}
                  {previewMedia.messagingPattern && (
                    <span className="px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-xs text-purple-400">
                      Messaging: {previewMedia.messagingPattern}
                    </span>
                  )}
                  {previewMedia.conceptPattern && (
                    <span className="px-3 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-xs text-emerald-400">
                      Concept: {previewMedia.conceptPattern}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Performance Metrics */}
              <div className="flex gap-4 ml-4">
                <div className="text-right">
                  <div className="text-xs text-zinc-500 mb-1">ROAS</div>
                  <div className="text-lg font-bold text-emerald-400">{previewMedia.roas?.toFixed(2)}x</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-zinc-500 mb-1">Spend</div>
                  <div className="text-lg font-bold text-emerald-400">${(previewMedia.totalSpend / 1000).toFixed(1)}K</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-zinc-500 mb-1">CTR</div>
                  <div className="text-lg font-bold text-emerald-400">{previewMedia.ctr?.toFixed(2)}%</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-zinc-500 mb-1">Revenue</div>
                  <div className="text-lg font-bold text-emerald-400">${(previewMedia.totalRevenue / 1000).toFixed(1)}K</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (view === 'deepdive' && selectedTrend) {
    return (
      <>
        <MediaPreviewModal />
        <div className="min-h-screen bg-zinc-950 text-white">
        {/* Header */}
        <div className="border-b border-zinc-800 bg-gradient-to-r from-zinc-900 to-zinc-950">
          <div className="max-w-7xl mx-auto px-8 py-6">
            <button
              onClick={() => {
                setView('gallery');
                setGeneratedConcepts(null);
              }}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-4"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">Back to Trends</span>
            </button>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 bg-gradient-to-br ${selectedTrend.color} rounded-2xl flex items-center justify-center text-3xl shadow-lg`}>
                  {selectedTrend.icon}
                </div>
                <div>
                  <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-zinc-400 bg-clip-text text-transparent mb-2">
                    {selectedTrend.name}
                  </h1>
                  <p className="text-zinc-400 text-lg">{selectedTrend.description}</p>
                  <div className="mt-2">
                    <span className="px-3 py-1 bg-zinc-800 rounded-full text-xs text-zinc-400 uppercase tracking-wider">
                      {selectedTrend.category} pattern • {getFilteredAds(selectedTrend.matchingAds).length} of {selectedTrend.matchingAds.length} ads shown
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-6 bg-zinc-900/50 px-6 py-4 rounded-xl border border-zinc-800">
                <div>
                  <div className="text-sm text-zinc-500 mb-1">Avg. ROAS</div>
                  <div className="text-2xl font-bold text-emerald-400">{selectedTrend.metrics.roas}x</div>
                </div>
                <div className="w-px h-12 bg-zinc-800"></div>
                <div>
                  <div className="text-sm text-zinc-500 mb-1">Total Ads</div>
                  <div className="text-2xl font-bold">{selectedTrend.metrics.totalAds}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-8 py-8">
          {/* Metrics Grid */}
          <div className="grid grid-cols-4 gap-6 mb-12">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="text-sm text-zinc-500 mb-2">Total Spend</div>
              <div className="text-2xl font-bold">${(selectedTrend.metrics.totalSpend / 1000).toFixed(1)}K</div>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="text-sm text-zinc-500 mb-2">Total Revenue</div>
              <div className="text-2xl font-bold">${(selectedTrend.metrics.totalRevenue / 1000).toFixed(1)}K</div>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="text-sm text-zinc-500 mb-2">CTR</div>
              <div className="text-2xl font-bold">{selectedTrend.metrics.ctr.toFixed(2)}%</div>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="text-sm text-zinc-500 mb-2">CVR</div>
              <div className="text-2xl font-bold">{selectedTrend.metrics.cvr.toFixed(2)}%</div>
            </div>
          </div>

          {/* Ad Gallery */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <span className="text-cyan-400">🎬</span>
                Ad Examples ({getFilteredAds(selectedTrend.matchingAds).length})
              </h2>
              
              {/* Media Type Filter Toggle */}
              <div className="inline-flex bg-zinc-900 rounded-xl p-1.5 border border-zinc-800">
                <button
                  onClick={() => setMediaTypeFilter('all')}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    mediaTypeFilter === 'all'
                      ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setMediaTypeFilter('image')}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    mediaTypeFilter === 'image'
                      ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  📷 Images
                </button>
                <button
                  onClick={() => setMediaTypeFilter('video')}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    mediaTypeFilter === 'video'
                      ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  🎥 Videos
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-6">
              {getFilteredAds(selectedTrend.matchingAds).map((ad, i) => (
                <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 hover:border-cyan-500/50 transition-all group">
                  <div 
                    className="aspect-[9/16] mb-4 cursor-pointer"
                    onClick={() => setPreviewMedia({
                      url: ad.asset_url,
                      type: ad.media_format,
                      adName: ad.ad_name,
                      campaignName: ad.campaign_name,
                      hookPattern: ad.hook_pattern,
                      messagingPattern: ad.messaging_pattern,
                      conceptPattern: ad.concept_pattern,
                      roas: ad.roas,
                      totalSpend: ad.total_spend,
                      totalRevenue: ad.total_revenue,
                      ctr: ad.ctr
                    })}
                  >
                    {renderMediaPreview(ad.asset_url, ad.media_format, ad.ad_name)}
                  </div>
                  <div className="space-y-3">
                    <div className="text-xs text-zinc-500 font-medium">{ad.campaign_name}</div>
                    <div className="text-sm text-zinc-300 line-clamp-2">{ad.ad_name}</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded text-xs text-emerald-400">
                        ROAS: {ad.roas.toFixed(2)}x
                      </span>
                      <span className="px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded text-xs text-emerald-400">
                        Spend: ${(ad.total_spend / 1000).toFixed(1)}K
                      </span>
                      {ad.ctr !== undefined && (
                        <span className="px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded text-xs text-emerald-400">
                          CTR: {ad.ctr.toFixed(2)}%
                        </span>
                      )}
                    </div>
                    {(ad.hook_pattern || ad.messaging_pattern || ad.concept_pattern) && (
                      <div className="text-xs text-zinc-500 mt-2 space-y-1">
                        {ad.hook_pattern && <div>Hook: {ad.hook_pattern}</div>}
                        {ad.messaging_pattern && <div>Messaging: {ad.messaging_pattern}</div>}
                        {ad.concept_pattern && <div>Concept: {ad.concept_pattern}</div>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Generate Variations */}
          <div className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 border border-cyan-500/30 rounded-2xl p-8 mb-12">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold mb-2">Generate New Variations</h3>
                <p className="text-zinc-400">
                  AI will create 5 new ad concepts following the <span className="text-cyan-400">{selectedTrend.name}</span> pattern
                </p>
              </div>
            </div>
            
            <button
              onClick={handleGenerateVariations}
              disabled={isGenerating}
              className="w-full py-5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 disabled:from-zinc-700 disabled:to-zinc-800 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] disabled:scale-100 disabled:cursor-not-allowed shadow-lg shadow-cyan-500/20"
            >
              {isGenerating ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Generating concepts...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span>Generate 5 New Ad Concepts</span>
                </div>
              )}
            </button>
          </div>

          {/* Generated Concepts */}
          {generatedConcepts && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <span className="text-cyan-400">✨</span>
                Generated Concepts
              </h2>
              <div className="grid grid-cols-1 gap-6">
                {generatedConcepts.map((concept) => (
                  <div key={concept.id} className="bg-zinc-900 border border-cyan-500/30 rounded-xl p-6 hover:border-cyan-500/50 transition-all">
                    <div className="flex items-start gap-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-xl font-bold flex-shrink-0">
                        {concept.id}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-4">{formatConceptField(concept.title)}</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
                            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Hook</div>
                            <div className="text-zinc-300 whitespace-pre-wrap">{formatConceptField(concept.hook)}</div>
                          </div>
                          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
                            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Visual</div>
                            <div className="text-zinc-300 whitespace-pre-wrap">{formatConceptField(concept.visual)}</div>
                          </div>
                          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
                            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Messaging</div>
                            <div className="text-zinc-300 whitespace-pre-wrap">{formatConceptField(concept.messaging)}</div>
                          </div>
                          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
                            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">CTA</div>
                            <div className="text-zinc-300 font-semibold whitespace-pre-wrap">{formatConceptField(concept.cta)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <MediaPreviewModal />
      <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-gradient-to-r from-zinc-900 to-zinc-950">
        <div className="max-w-7xl mx-auto px-8 py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-5xl font-bold bg-gradient-to-r from-white via-zinc-200 to-zinc-400 bg-clip-text text-transparent mb-3">
                AI Trend Analytics
              </h1>
              <p className="text-zinc-400 text-lg">Discover patterns in Hook, Messaging & Concept strategies</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-zinc-500 mb-1">Total Trends</div>
              <div className="text-4xl font-bold text-cyan-400">{trends.length}</div>
            </div>
          </div>

          {/* Category Toggle */}
          <div className="mb-6">
            <div className="inline-flex bg-zinc-900 rounded-xl p-1.5 border border-zinc-800">
              <button
                onClick={() => setCategory('hook')}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  category === 'hook'
                    ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                🎣 Hook Patterns
              </button>
              <button
                onClick={() => setCategory('messaging')}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  category === 'messaging'
                    ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                💬 Messaging Patterns
              </button>
              <button
                onClick={() => setCategory('concept')}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  category === 'concept'
                    ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                📝 Concept Patterns
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <h3 className="font-semibold text-lg">Filters</h3>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Ad Name Contains</label>
                <input
                  type="text"
                  value={filterInputs.adName}
                  onChange={(e) => setFilterInputs({ ...filterInputs, adName: e.target.value })}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleApplyFilters();
                    }
                  }}
                  placeholder="Search ad names..."
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Campaign Name Contains</label>
                <input
                  type="text"
                  value={filterInputs.campaignName}
                  onChange={(e) => setFilterInputs({ ...filterInputs, campaignName: e.target.value })}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleApplyFilters();
                    }
                  }}
                  placeholder="Search campaigns..."
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Min Spend</label>
                <input
                  type="number"
                  value={filterInputs.minSpend}
                  onChange={(e) => setFilterInputs({ ...filterInputs, minSpend: e.target.value })}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleApplyFilters();
                    }
                  }}
                  placeholder="0"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Min ROAS</label>
                <input
                  type="number"
                  step="0.1"
                  value={filterInputs.minROAS}
                  onChange={(e) => setFilterInputs({ ...filterInputs, minROAS: e.target.value })}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleApplyFilters();
                    }
                  }}
                  placeholder="0"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={handleApplyFilters}
                className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-medium rounded-lg transition-all duration-200 flex items-center gap-2 shadow-lg shadow-cyan-500/20"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Apply Filters
              </button>
              {(appliedFilters.adName || appliedFilters.campaignName || appliedFilters.minSpend > 0 || appliedFilters.minROAS > 0) && (
                <button
                  onClick={handleClearFilters}
                  className="text-sm text-zinc-400 hover:text-cyan-400 flex items-center gap-2 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear all filters
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Trends Grid */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        {isLoading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-zinc-400">Loading trends...</p>
          </div>
        ) : trends.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold mb-2">No trends found</h3>
            <p className="text-zinc-400 mb-6">Try adjusting your filters or select a different category</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {trends.map((trend) => (
              <div
                key={trend.id}
                onClick={() => {
                  setSelectedTrend(trend);
                  setView('deepdive');
                }}
                className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 hover:border-cyan-500/50 transition-all cursor-pointer group"
              >
                <div className="flex items-start gap-6">
                  {/* Icon - Use first ad's media */}
                  {renderPatternIcon(trend)}

                  {/* Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-2xl font-bold mb-2">{trend.name}</h3>
                        <p className="text-zinc-400 mb-3 text-sm leading-relaxed">{trend.description}</p>
                        <span className="px-3 py-1 bg-zinc-800 rounded-full text-xs text-zinc-400 uppercase tracking-wider">
                          {trend.category} pattern
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-zinc-500 mb-1">Avg. ROAS</div>
                        <div className="text-3xl font-bold text-emerald-400">{trend.metrics.roas}x</div>
                        <div className="text-sm text-zinc-500 mb-1 mt-3">Spend</div>
                        <div className="text-xl font-bold text-emerald-400">${(trend.metrics.totalSpend / 1000).toFixed(1)}K</div>
                        <div className="text-sm text-zinc-500 mb-1 mt-3">CTR</div>
                        <div className="text-xl font-bold text-emerald-400">{(trend.metrics.ctr || 0).toFixed(2)}%</div>
                      </div>
                    </div>

                    {/* Metrics Grid */}
                    <div className="grid grid-cols-4 gap-6">
                      <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5">
                        <div className="text-sm text-zinc-500 mb-2">Total Ads</div>
                        <div className="text-2xl font-bold">{trend.metrics.totalAds}</div>
                      </div>
                      <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5">
                        <div className="text-sm text-zinc-500 mb-2">Total Spend</div>
                        <div className="text-2xl font-bold">${(trend.metrics.totalSpend / 1000).toFixed(1)}K</div>
                      </div>
                      <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5">
                        <div className="text-sm text-zinc-500 mb-2">Total Revenue</div>
                        <div className="text-2xl font-bold">${(trend.metrics.totalRevenue / 1000).toFixed(1)}K</div>
                      </div>
                      <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5">
                        <div className="text-sm text-zinc-500 mb-2">CTR</div>
                        <div className="text-2xl font-bold">{trend.metrics.ctr.toFixed(2)}%</div>
                      </div>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default TrendAnalytics;

